/**
 * @file vehicles.api.js
 * @module VehiclesAPI
 * @description HTTP API wrapper for vehicle related endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios";

export const VehiclesAPI = {
    /**
     * Retrieve vehicles with pagination and optional query.
     * @param {number} limit - Number of items per page.
     * @param {number} page - Page number.
     * @param {string} [query=""] - Optional query string for filtering.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to vehicles list.
     */
    getVehicles: async (limit, page, query = "", token = "") => {
        return await axiosInstance(token).get(`/vehicles?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Get a single vehicle by id.
     * @param {string} vehicleId - Vehicle identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to vehicle data.
     */
    getVehicleById: async (vehicleId, token = "") => {
        return await axiosInstance(token).get(`/vehicles/${vehicleId}`);
    },

    /**
     * Create a new vehicle.
     * @param {Object} vehicle - Vehicle payload.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to created vehicle.
     */
    createVehicle: async (vehicle, token = "") => {
        return await axiosInstance(token).post(`/vehicles`, vehicle);
    },

    /**
     * Update a vehicle by id.
     * @param {string} vehicleId - Vehicle identifier.
     * @param {Object} vehicle - Updated vehicle payload.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated vehicle.
     */
    updateVehicleById: async (vehicleId, vehicle, token = "") => {
        return await axiosInstance(token).put(`/vehicles/${vehicleId}`, vehicle);
    },

    /**
     * Delete a vehicle by id.
     * @param {string} vehicleId - Vehicle identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating deletion result.
     */
    deleteVehicleById: async (vehicleId, token = "") => {
        return await axiosInstance(token).delete(`/vehicles/${vehicleId}`);
    },

    /**
     * Delete many vehicles by ids.
     * @param {Array<string>|Object} vehicles - Array of vehicle ids or payload accepted by backend.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating deletion result.
     */
    deleteManyVehiclesById: async (vehicles, token = "") => {
        return await axiosInstance(token).put(`/bulk-delete-vehicles`, { _ids: vehicles });
    },

    /**
     * Get vehicles for a specific branch.
     * @param {string} branchId - Branch identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to branch vehicles.
     */
    getVehiclesByBranchId: async (branchId, token = "") => {
        return await axiosInstance(token).get(`/branches/${branchId}/vehicles`);
    },

    /**
     * Patch attributes of a vehicle by id.
     * @param {string} vehicleId - Vehicle identifier.
     * @param {Object} body - Partial payload with attributes to update.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated vehicle.
     */
    updateAnyAttributeById: async (vehicleId, body, token = "") => {
        return await axiosInstance(token).patch(`/vehicles/${vehicleId}`, body);
    },

    /**
     * Upload a multipart file (insurance picture).
     * @param {FormData|Object} body - FormData containing file fields.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to upload result.
     */
    uploadFIle: async (body, token = "") => {
        return await axiosInstance(token).post(`/picture/upload/insurance`, body, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },

    /**
     * Export vehicles to CSV (request payload can vary by backend).
     * @param {Array|Object} vehicles - Payload describing vehicles to export.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to export initiation/result.
     */
    downloadCVS: async (vehicles, token = "") => {
        return await axiosInstance(token).post(`/export/vehicles`, vehicles);
    },

    /**
     * Check whether a vehicle is assigned to a rate.
     * @param {string} vehicleId - Vehicle identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to boolean-like result.
     */
    isAssigned: async (vehicleId, token = "") => {
        return await axiosInstance(token).get(`/vehicles/${vehicleId}/isAssignedToTheRate`);
    },

    /**
     * Check license plate duplication for a branch.
     * @param {string} branchId - Branch identifier.
     * @param {string} licensePlate - License plate string to check.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to duplication check result.
     */
    licensePlateDuplicationCheck: async (branchId, licensePlate, token = "") => {
        return await axiosInstance(token).post(`/license-plate-duplication-check`, { branchId: branchId, licensePlate: licensePlate });
    },

    /**
     * Get cars options (helper endpoint).
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to cars options.
     */
    getCarsOptions: (token = "") => {
        return axiosInstance(token).get(`/cars-options`);
    },

    /**
     * Retrieve cars with pagination and optional query.
     * @param {number} limit - Number of items per page.
     * @param {number} page - Page number.
     * @param {string} [query=""] - Optional query string.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to cars list.
     */
    getCars: (limit, page, query = "", token = "") => {
        return axiosInstance(token).get(`/cars?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Get car by id.
     * @param {string} carId - Car identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to car data.
     */
    getCarById: (carId, token = "") => {
        return axiosInstance(token).get(`/cars/${carId}`);
    },

    /**
     * Get car by slug.
     * @param {string} slug - Car slug.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to car data.
     */
    getCarBySlug: (slug, token = "") => {
        return axiosInstance(token).get(`/cars/slugs/${slug}`);
    },

    /**
     * Update a car by id.
     * @param {string} carId - Car identifier.
     * @param {Object} car - Car update payload.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated car.
     */
    updateCarById: (carId, car, token = "") => {
        return axiosInstance(token).patch(`/cars/${carId}`, car);
    },

    /**
     * Find cars by details (type, make, model).
     * @param {string} type
     * @param {string} make
     * @param {string} model
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to finder results.
     */
    getCarsByDetails: (type, make, model, token = "") => {
        return axiosInstance(token).post(`/car-finder`, { type: type, make: make, model: model });
    },

    /**
     * Verify image usage across resources.
     * @param {Array} images - Array of image identifiers or URLs.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to verification results.
     */
    verifyImageUsage: (images = [], token = "") => {
        return axiosInstance(token).post(`/verify-image-usage`, { images: images });
    }
}